import React, { useState, useContext } from 'react';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import {
  Rating, Loader
} from '@thd-olt-component-react/core-ui';
import { Dropdown, MenuItem } from '@one-thd/sui-atomic-components';
import { useDataModel } from '@thd-nucleus/data-sources';
import { Pagination } from '@thd-olt-component-react/pagination';
import { string, shape, bool } from 'prop-types';
import { dataModel } from '../dataModel';
import { validateResponse } from '../validation';

const PAGE_SIZE = 5;
const formatDateAndTime = (value) => (
  new Date(value).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
);

const EachReview = ({ item }) => (
  <>
    <div className="sui-flex sui-justify-between">
      <Rating
        data-testid="ratings"
        value={item.rating}
      />
      <div data-testid="Timestamp">
        {formatDateAndTime(item.createdTime)}
      </div>
    </div>

    <div
      data-testid="review-body"
      className="sui-mb-5 sui-pb-5 sui-border-b-1 sui-border-solid sui-overflow-hidden sui-border-primary"
    >
      {item.body}
    </div>
  </>
);
EachReview.displayName = 'EachReview';
EachReview.propTypes = {
  item: shape({}).isRequired
};

const SORT_OPTIONS = [
  {
    name: 'Distance',
    value: 'location_proximity,+created_time+desc'
  },
  {
    name: 'Newest',
    value: 'created_time+desc'
  },
  {
    name: 'Oldest',
    value: 'created_time+asc'
  },
  {
    name: 'Highest Rating',
    value: 'rating+desc,+created_time+desc'
  },
  {
    name: 'Lowest Rating',
    value: 'rating+asc,+created_time+desc'
  }
];

const Review = ({ serviceIdentifier, isDrawerOpen }) => {

  const [page, setPage] = useState(1);
  const [sort, setSort] = useState(SORT_OPTIONS[0].value);
  const { localStoreZip } = useContext(ExperienceContext);

  // gql call for pagination
  const { data, loading, error } = useDataModel('techShedReviews', {
    variables: {
      serviceIdentifier,
      size: PAGE_SIZE,
      from: (page - 1) * PAGE_SIZE,
      zipcode: localStoreZip,
      sort
    },
    ssr: false,
    skip: !isDrawerOpen
  });

  if (loading) {
    return (
      <div className="sui-flex sui-justify-center sui-items-center sui-h-1/2">
        <Loader active />
      </div>
    );
  }

  if (validateResponse(data, error, serviceIdentifier)) {
    return (
      <p
        className="sui-flex sui-justify-center sui-items-center sui-font-bold sui-h-1/2 sui-text-5xl"
      >
        No reviews for this product
      </p>
    );
  }

  const { techShedReviews: {
    reviews = [],
    total
  } } = data;

  return (
    <>
      <div className="sui-flex sui-items-center sui-ml-5 sui-mt-5">
        Sort By:
        <div className="sui-w-40 sui-ml-7">
          <Dropdown
            value={sort}
            onChange={(option) => {
              setSort(option.target.value);
              setPage(1);
            }}
          >
            {
              SORT_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>{option.name}</MenuItem>
              ))
            }
          </Dropdown>
        </div>
      </div>
      <div data-testid="reviews" className="sui-flex sui-flex-col sui-mx-5 sui-mt-5">
        {reviews.map((item, index) => <EachReview item={item} key={`review-${index}`} />)}
      </div>
      <Pagination
        currentPage={page}
        onPageChanged={(event) => setPage(event)}
        showArrows
        totalPages={Math.ceil(total / PAGE_SIZE)}
        pageSize={PAGE_SIZE}
      />
    </>
  );

};

Review.displayName = 'Review';
Review.dataModel = dataModel;
Review.propTypes = {
  serviceIdentifier: string.isRequired,
  isDrawerOpen: bool.isRequired
};
export { Review };